import { FNADetails } from './analyses';
import { UPLOADED_DOC_LABEL } from '../../reports/hooks/useReportUpdater';

export enum ReportState {
  Active = 1,
  Archived = 2,
  Generated = 3,
}

// Key used to find report language in properties
export const REPORT_LANGUAGE_PROPERTY_LABEL = 'reportLanguage';
// Key used to find report status in properties
export const REPORT_STATUS_PROPERTY_LABEL = 'reportBuilderStatus';
// Key used to find report zip file
export const REPORT_ZIP_FILE_LABEL = 'report-zip';
// Keys used to find report section pdf file
export const REPORT_SECTION_FILE_LABELS = [
  'section-report',
  'section-report-buyandsell',
  'section-report-keyperson',
  'section-report-suretyship',
  UPLOADED_DOC_LABEL,
];

export interface ReportClient {
  id: number;
  reportID: number;
  roleplayerID: number;
  roleplayerTypeID: number;
  roleplayerTypeName: string;
  roleplayerTypeNote: string;
  roleplayerStateID: number;
  roleplayerStateName: string;
  roleplayerStateNote: string;
  roleplayerName: string;
}

export interface ReportFna {
  id: number;
  name: string;
  date: Date;
  description: string;
}

export interface ReportQuote {
  id: number;
  name: string;
  date: Date;
  taxStatus: string;
  quote: string;
  need: string;
}

export interface ReportFile {
  id: number;
  reportID: number;
  sectionID?: number;
  fileID: number;
  fileToken: string;
  fileLabel: string;
  fileUrl: string;
  fileUploadName: string;
  fileExtensionID: number;
  fileStateID: number;
  fileStateName: string;
  fileStateNote: string;
  fileTypeID: number;
  fileTypeName: string;
  fileTypeNote: string;
  extensionName: string;
  extensionTypeID: number;
  extensionTypeName: string;
  extensionTypeNote: string;
}

export interface ReportProperty {
  id: number;
  reportID: number;
  propertyID: number;
  propertyLabel: string;
  propertyValue: string;
  propertyTypeID: number;
  propertyTypeName: string;
  propertyTypeNote: string;
  sectionName: string;
  sectionNote: string;
  sectionID: number;
  propertySectionID: number;
}

/**
 * Map of report sections to their name (we use name to identify the section in the report)
 */
export enum ReportSectionName {
  CoverPage = 'Front page',
  Disclosure = 'Disclosure',
  ExecSummary = 'Executive summary',
  Fna = 'Financial Needs Analysis (FNA)',
  Quote = 'Quote/s',
  Agreement = 'Agreements',
  RecordOfAdvice = 'Record of advice',
  SLA = 'Service agreement',
}

// List of sections that are required FNA to be selected
export const sectionNamesRequiredFna: Array<ReportSectionName> = [
  ReportSectionName.Fna,
  ReportSectionName.Quote,
];

export interface ReportSection {
  id: number;
  reportID: number;
  sectionID: number;
  sectionName: ReportSectionName;
  sectionNote: string;
  properties: ReportProperty[];
  files: ReportFile[];
}

export interface Report {
  id: number;
  agentID: number;
  name: string;
  note: string;
  desc: string;
  summaryComments: string;
  fnaComments: string;
  quoteComments: string;
  slaComments: string;
  adviceComments: string;
  stateID: ReportState;
  stateName: string;
  stateNote: string;
  sections: ReportSection[];
  roleplayers: ReportClient[];
  fnAs: FNADetails[];
  quotes: ReportQuote[];
  properties: ReportProperty[];
  files: ReportFile[];
}

export interface ReportItem {
  id: number;
  adviceComments?: string;
  agentID: number;
  created: string;
  desc?: string;
  fnaComments?: string;
  name?: string;
  note?: string;
  quoteComments?: string;
  slaComments?: string;
  stateID: ReportState;
  stateName: string;
  stateNote: string;
  summaryComments?: string;
}
